import { Button, Col, Collapse, Divider, Row, Tabs } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import GeneralSettingContext from "../../../context/general-setting/context";
import GeneralSettingContextProvider from "../../../context/general-setting/provider";
import { IUpdateGeneralSetting } from "../../../models/general-setting/request";
import { languages } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import { getTranslations } from "../../../utils/helpers/translations";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import { MediumFor } from "../../../models/medium/enum";
import EndPoints from "../../../services/end-points";
import { IDiscountCode } from "../../../models/discount-code/response";
import RichEditor from "../../form-components/rich-text-editor";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { execute } from "../../../utils/helpers/execute";
import styles from "./style.module.scss";
import { useNavigate } from "react-router-dom";
import { IMedium } from "../../../models/medium/response";
import ImageFieldBlock from "../../general/image-field-block";
import { errorNotification, successNotification } from "../../../utils/helpers/notification";
import {
    DollarOutlined,
    CarOutlined,
    MobileOutlined,
    GiftOutlined,
    TeamOutlined,
} from "@ant-design/icons";

const Form = () => {
    const { t } = useTranslation();
    const [discountCode, setDiscountCode] = useState<IDiscountCode[]>([]);
    const [discountCodeLoading, setDiscountCodeLoading] = useState(true);
    const { direction } = useContext(AppContext);
    const { list, actions, loading } = useContext(GeneralSettingContext);
    const navigate = useNavigate();
    const [hasMore, setHasMore] = useState(true);
    const [query, setQuery] = useState({ page: 1, search: undefined });
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

    const [newImages, setNewImages] = useState<Record<string, string | null>>({});
    const [imageChanged, setImageChanged] = useState<Record<string, boolean>>({});
    const [withoutBackgrounds, setWithoutBackgrounds] = useState<Record<string, boolean>>({});

    const handleImageChange = (fieldName: string) => (response: IMedium | null) => {
        if (response) {
            setNewImages((prev) => ({ ...prev, [fieldName]: response.url }));
        } else {
            setNewImages((prev) => ({ ...prev, [fieldName]: null }));
        }
    };

    const handleUploadNewImage = async (fieldName: string) => {
        const newImageUrl = newImages[fieldName];
        if (!newImageUrl) {
            errorNotification(t("no_new_image_to_upload"));
            return;
        }
        try {
            const response = await fetch(newImageUrl);
            const blob = await response.blob();
            const file = new File([blob], `${fieldName}.png`, { type: "image/png" });

            const formData = new FormData();
            formData.append("medium", file);
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);
            formData.append("without_background", String(withoutBackgrounds[fieldName] ?? false));

            const uploadResponse = await EndPoints.media.uploadMedia(formData);

            setValue(fieldName, uploadResponse.id, { shouldDirty: true });
            setImageChanged((prev) => ({ ...prev, [fieldName]: false }));

            successNotification(t("image_uploaded_successfully"));
        } catch (error) {
            errorNotification(t("failed_to_upload_image"));
        }
    };

    const handleUpdateBackground = async (fieldName: string, imageId: number) => {
        try {
            const withoutBackground = withoutBackgrounds[fieldName];
            await EndPoints.media.updateMedia(imageId, Boolean(withoutBackground));
            successNotification(t("background_type_updated_successfully"));
        } catch (error) {
            errorNotification(t("failed_to_update_background_type"));
        }
    };

    const methods = useForm<any>({
        defaultValues: {
            sharingAppLink: list?.sharingAppLink ?? '',
            sharingAppMessage: getTranslations(list?.translations.sharingAppMessage),
            activatingPublicService: list?.activatingPublicService ?? false,
            activatePublicMessageClient: list?.activatePublicMessageClient ?? false,
            publicMessageClient: getTranslations(list?.translations.publicMessageClient),
            activatePublicMessagePharmacist: list?.activatePublicMessagePharmacist ?? false,
            publicMessagePharmacist: getTranslations(list?.translations.publicMessagePharmacist),
            activatePublicMessageDelivery: list?.activatePublicMessageDelivery ?? false,
            publicMessageDelivery: getTranslations(list?.translations?.publicMessageDelivery),
            maxNumberPharmaciesFindMedicine: list?.maxNumberPharmaciesFindMedicine,
            scoreValidity: list?.scoreValidity,
            chargingCustomerForReachStartCostOrder: true,
            kilometerCost: list?.kilometerCost,
            priceKilometerCustomer: list?.priceKilometerCustomer,
            currencySymbol: getTranslations(list?.translations?.currencySymbol),
            maxNumberPharmaciesFindPharmacie: list?.maxNumberPharmaciesFindPharmacie,
            pointMultiplicationFactor: list?.pointMultiplicationFactor,
            numberTimesIgnoredAllowed: list?.numberTimesIgnoredAllowed,
            showListPharmacies: list?.showListPharmacies,
            commission: list?.commission,
            deliveryWorkerAcceptanceTime: list?.deliveryWorkerAcceptanceTime,
            pharmacyAcceptanceTime: list?.pharmacyAcceptanceTime,
            timeBroadcast: list?.timeBroadcast,
            freeDelivery: list?.freeDelivery,
            freeDeliveryPharmacyPromotion: list?.freeDeliveryPharmacyPromotion,
            driverTimeBroadcast: list?.driverTimeBroadcast,
            maxDriversInOrder: list?.maxDriversInOrder,
            radiusSearchForDriver: list?.radiusSearchForDriver,
            homeHeaderClientEn: list?.homeHeaderClientEn?.id ?? null,
            homeHeaderClientAr: list?.homeHeaderClientAr?.id ?? null,
            homeHeaderDriverEn: list?.homeHeaderDriverEn?.id ?? null,
            homeHeaderDriverAr: list?.homeHeaderDriverAr?.id ?? null,
            homeHeaderInvestorEn: list?.homeHeaderInvestorEn?.id ?? null,
            homeHeaderInvestorAr: list?.homeHeaderInvestorAr?.id ?? null,
            homeHeaderClient: list?.homeHeaderClient?.id ?? null,
            homeHeaderDriver: list?.homeHeaderDriver?.id ?? null,
            homeHeaderInvestor: list?.homeHeaderInvestor?.id ?? null,
            facebook: list?.facebook,
            loyaltyProgramLink: list?.loyaltyProgramLink,
            delayedOrderSchedulingTime: list?.delayedOrderSchedulingTime,
            phone: list?.phone,
            telegram: list?.telegram,
            whatsApp: list?.whatsApp,
            instagram: list?.instagram,
            minimumLimit: list?.minimumLimit,
            upperLimit: list?.upperLimit,
            firstOrderFree: list?.firstOrderFree ?? false,
            clientAcceptanceTime: Number(list?.clientAcceptanceTime),
            pointPrice: list?.pointPrice,
            showMedicalSpecialties: list?.showMedicalSpecialties ?? false,
            showCompanies: list?.showCompanies ?? false,
            welcomeCouponActive: list?.welcomeCouponActive ?? false,
            welcomeCoupon: list?.welcomeCoupon?.id,
            welcomeMessageActivation: list?.welcomeMessageActivation ?? false,
            welcomeMessage: getTranslations(list?.translations?.welcomeMessage),
            maxNumOfUnResponseOrder: list?.maxNumOfUnResponseOrder,
            hotLine: list?.hotLine,
            fixedDeliveryPriceOnClient: list?.fixedDeliveryPriceOnClient,
            makeInvestorsNoResponseOffVisibility: list?.makeInvestorsNoResponseOffVisibility ?? false,
            ordersSummeryInvestorVisibility: list?.ordersSummeryInvestorVisibility ?? false,
            ordersSummeryGuestInvestorVisibility: list?.ordersSummeryGuestInvestorVisibility ?? false,
            startDateTimeOrdersSummeryInvestor: list?.startDateTimeOrdersSummeryInvestor ?? null,
            endDateTimeOrdersSummeryInvestor: list?.endDateTimeOrdersSummeryInvestor ?? null,
            startDateTimeOrdersGuestSummeryInvestor: list?.startDateTimeOrdersGuestSummeryInvestor ?? null,
            endDateTimeOrdersGuestSummeryInvestor: list?.endDateTimeOrdersGuestSummeryInvestor ?? null,
            circleRadiusMeters: list?.circleRadiusMeters ?? 0,
            exchangePrice: list?.exchangePrice ?? 0,
            priceTotalProductsToGetFreeDelivery: list?.priceTotalProductsToGetFreeDelivery ?? 0,
            enableInvitation: list?.enableInvitation ?? false,
            messageForSharingInvitation: getTranslations(list?.translations?.messageForSharingInvitation),
            smallMessageForInvitation: getTranslations(list?.translations?.smallMessageForInvitation),
            bigMessageForInvitation: getTranslations(list?.translations?.bigMessageForInvitation),
            titleInvitationSender: getTranslations(list?.translations?.titleInvitationSender),
            descriptionInvitationSender: getTranslations(list?.translations?.descriptionInvitationSender),
            imageIdSender: list?.imageIdSender?.id,
            imageIdForBigMessage: list?.imageIdForBigMessage?.id,
            imageIdForSmallMessage: list?.imageIdForSmallMessage?.id,
            titleInvitationReceiver: getTranslations(list?.translations?.titleInvitationReceiver),
            descriptionInvitationReceiver: getTranslations(list?.translations?.descriptionInvitationReceiver),
            imageIdReceiver: list?.imageIdReceiver?.id,
            verySmallMessageForInvitation: getTranslations(list?.translations?.verySmallMessageForInvitation) ?? null,
            imageIdForVerySmallMessage: list?.imageIdForVerySmallMessage?.id,
            dailyPaymentLimit: list?.dailyPaymentLimit ?? '',
            weeklyPaymentLimit: list?.weeklyPaymentLimit ?? '',
            monthlyPaymentLimit: list?.monthlyPaymentLimit ?? '',
            isEPaymentEnabled: list?.isEPaymentEnabled ?? false,
            isAlbarakaPaymentEnabled: list?.isAlbarakaPaymentEnabled ?? false,
            isAlshamPaymentEnable: list?.isAlshamPaymentEnable ?? false,
            expressDeliveryPrice: list?.expressDeliveryPrice ?? undefined,
            expressDeliveryService: list?.expressDeliveryService ?? false,
            fixedDeliveryTime: list?.fixedDeliveryTime ?? undefined,
            expressDeliveryTime: list?.expressDeliveryTime ?? undefined,
            newProductPeriodDays: list?.newProductPeriodDays ?? undefined,
            useTripBasedDelivery: list?.useTripBasedDelivery ?? false,
            includeReturnTripInPayment: list?.includeReturnTripInPayment ?? false,
            routeStrategy: list?.routeStrategy ?? 1,
            otpServiceUnavailable: list?.otpServiceUnavailable ?? false,
            enableRouteComparison: list?.enableRouteComparison ?? false,
        } as IUpdateGeneralSetting,
    });

    const { handleSubmit, reset, control, setValue } = methods;

    const handleImageDelete = async (fieldName: string) => {
        try {
            setImageLoading(prev => ({ ...prev, [fieldName]: true }));
            methods.setValue(fieldName, null);
            if (list) {
                list[fieldName] = null;
            }
        } finally {
            setImageLoading(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    useEffect(() => {
        reset({
            sharingAppLink: list?.sharingAppLink ?? '',
            sharingAppMessage: getTranslations(list?.translations.sharingAppMessage),
            activatingPublicService: list?.activatingPublicService ?? false,
            activatePublicMessageClient: list?.activatePublicMessageClient ?? false,
            publicMessageClient: getTranslations(list?.translations.publicMessageClient),
            activatePublicMessagePharmacist: list?.activatePublicMessagePharmacist ?? false,
            publicMessagePharmacist: getTranslations(list?.translations.publicMessagePharmacist),
            activatePublicMessageDelivery: list?.activatePublicMessageDelivery ?? false,
            publicMessageDelivery: getTranslations(list?.translations?.publicMessageDelivery),
            maxNumberPharmaciesFindMedicine: list?.maxNumberPharmaciesFindMedicine,
            scoreValidity: list?.scoreValidity,
            chargingCustomerForReachStartCostOrder: true,
            kilometerCost: list?.kilometerCost,
            priceKilometerCustomer: list?.priceKilometerCustomer,
            currencySymbol: getTranslations(list?.translations?.currencySymbol),
            maxNumberPharmaciesFindPharmacie: list?.maxNumberPharmaciesFindPharmacie,
            pointMultiplicationFactor: list?.pointMultiplicationFactor,
            numberTimesIgnoredAllowed: list?.numberTimesIgnoredAllowed,
            showListPharmacies: list?.showListPharmacies,
            commission: list?.commission,
            deliveryWorkerAcceptanceTime: list?.deliveryWorkerAcceptanceTime,
            pharmacyAcceptanceTime: list?.pharmacyAcceptanceTime,
            timeBroadcast: list?.timeBroadcast,
            freeDelivery: list?.freeDelivery,
            priceTotalProductsToGetFreeDelivery: list?.priceTotalProductsToGetFreeDelivery,
            freeDeliveryPharmacyPromotion: list?.freeDeliveryPharmacyPromotion,
            driverTimeBroadcast: list?.driverTimeBroadcast,
            maxDriversInOrder: list?.maxDriversInOrder,
            radiusSearchForDriver: list?.radiusSearchForDriver,
            homeHeaderClientEn: list?.homeHeaderClientEn?.id ?? null,
            homeHeaderClientAr: list?.homeHeaderClientAr?.id ?? null,
            homeHeaderDriverEn: list?.homeHeaderDriverEn?.id ?? null,
            homeHeaderDriverAr: list?.homeHeaderDriverAr?.id ?? null,
            homeHeaderInvestorEn: list?.homeHeaderInvestorEn?.id ?? null,
            homeHeaderInvestorAr: list?.homeHeaderInvestorAr?.id ?? null,
            homeHeaderClient: list?.homeHeaderClient?.id ?? null,
            homeHeaderDriver: list?.homeHeaderDriver?.id ?? null,
            homeHeaderInvestor: list?.homeHeaderInvestor?.id ?? null,
            facebook: list?.facebook,
            loyaltyProgramLink: list?.loyaltyProgramLink,
            delayedOrderSchedulingTime: list?.delayedOrderSchedulingTime,
            phone: list?.phone,
            telegram: list?.telegram,
            whatsApp: list?.whatsApp,
            instagram: list?.instagram,
            minimumLimit: list?.minimumLimit,
            upperLimit: list?.upperLimit,
            firstOrderFree: list?.firstOrderFree ?? false,
            clientAcceptanceTime: Number(list?.clientAcceptanceTime),
            pointPrice: list?.pointPrice,
            showMedicalSpecialties: list?.showMedicalSpecialties ?? false,
            showCompanies: list?.showCompanies ?? false,
            welcomeCouponActive: list?.welcomeCouponActive ?? false,
            welcomeCoupon: list?.welcomeCoupon?.id,
            welcomeMessageActivation: list?.welcomeMessageActivation ?? false,
            welcomeMessage: getTranslations(list?.translations?.welcomeMessage),
            maxNumOfUnResponseOrder: list?.maxNumOfUnResponseOrder,
            hotLine: list?.hotLine,
            fixedDeliveryPriceOnClient: list?.fixedDeliveryPriceOnClient,
            makeInvestorsNoResponseOffVisibility: list?.makeInvestorsNoResponseOffVisibility ?? false,
            ordersSummeryInvestorVisibility: list?.ordersSummeryInvestorVisibility ?? false,
            ordersSummeryGuestInvestorVisibility: list?.ordersSummeryGuestInvestorVisibility ?? false,
            startDateTimeOrdersSummeryInvestor: list?.startDateTimeOrdersSummeryInvestor ?? null,
            endDateTimeOrdersSummeryInvestor: list?.endDateTimeOrdersSummeryInvestor ?? null,
            startDateTimeOrdersGuestSummeryInvestor: list?.startDateTimeOrdersGuestSummeryInvestor ?? null,
            endDateTimeOrdersGuestSummeryInvestor: list?.endDateTimeOrdersGuestSummeryInvestor ?? null,
            circleRadiusMeters: list?.circleRadiusMeters ?? 0,
            exchangePrice: list?.exchangePrice ?? 0,
            enableInvitation: list?.enableInvitation ?? false,
            messageForSharingInvitation: getTranslations(list?.translations?.messageForSharingInvitation),
            smallMessageForInvitation: getTranslations(list?.translations?.smallMessageForInvitation),
            bigMessageForInvitation: getTranslations(list?.translations?.bigMessageForInvitation),
            titleInvitationSender: getTranslations(list?.translations?.titleInvitationSender),
            descriptionInvitationSender: getTranslations(list?.translations?.descriptionInvitationSender),
            imageIdSender: list?.imageIdSender?.id,
            imageIdForBigMessage: list?.imageIdForBigMessage?.id,
            imageIdForSmallMessage: list?.imageIdForSmallMessage?.id,
            imageIdReceiver: list?.imageIdReceiver?.id,
            imageIdForVerySmallMessage: list?.imageIdForVerySmallMessage?.id,
            titleInvitationReceiver: getTranslations(list?.translations?.titleInvitationReceiver),
            descriptionInvitationReceiver: getTranslations(list?.translations?.descriptionInvitationReceiver),
            verySmallMessageForInvitation: getTranslations(list?.translations?.verySmallMessageForInvitation) ?? null,
            dailyPaymentLimit: list?.dailyPaymentLimit ?? '',
            weeklyPaymentLimit: list?.weeklyPaymentLimit ?? '',
            monthlyPaymentLimit: list?.monthlyPaymentLimit ?? '',
            isEPaymentEnabled: list?.isEPaymentEnabled ?? false,
            isAlbarakaPaymentEnabled: list?.isAlbarakaPaymentEnabled ?? false,
            isAlshamPaymentEnable: list?.isAlshamPaymentEnable ?? false,
            expressDeliveryPrice: list?.expressDeliveryPrice ?? undefined,
            expressDeliveryService: list?.expressDeliveryService ?? false,
            fixedDeliveryTime: list?.fixedDeliveryTime ?? undefined,
            expressDeliveryTime: list?.expressDeliveryTime ?? undefined,
            newProductPeriodDays: list?.newProductPeriodDays ?? undefined,
            useTripBasedDelivery: list?.useTripBasedDelivery ?? false,
            includeReturnTripInPayment: list?.includeReturnTripInPayment ?? false,
            routeStrategy: list?.routeStrategy ?? 1,
            otpServiceUnavailable: list?.otpServiceUnavailable ?? false,
            enableRouteComparison: list?.enableRouteComparison ?? false,
        } as IUpdateGeneralSetting);

        const newWithoutBackgrounds: Record<string, boolean> = {};
        fieldNames.forEach((field) => {
            newWithoutBackgrounds[field] = Boolean(list?.[field]?.without_background);
        });
        setWithoutBackgrounds(newWithoutBackgrounds);
    }, [list]);

    const onSubmit = async (data: any) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                await actions.updateGeneralSetting({
                    ...data,
                    clientAcceptanceTime: Number(data?.clientAcceptanceTime),
                    routeStrategy: data?.routeStrategy ? Number(data.routeStrategy) : 1,
                    welcomeCoupon: data?.welcomeCoupon || null,
                });
                navigate(-1);
            },
            fallback: (error) => { },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    useEffect(() => {
        const getGeneralSetting = async () => {
            await actions.getGeneralSetting();
        };
        getGeneralSetting();
    }, [direction]);

    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop === clientHeight && hasMore && !discountCodeLoading) {
            setQuery({ ...query, page: query.page + 1 });
        }
    };

    const handleSearch = (value) => {
        setQuery({ page: 1, search: value });
        setDiscountCode([]);
    };

    const getDiscountCode = async () => {
        try {
            const { data } = await EndPoints.discountCode.getAllDiscountCode({
                light: true,
                page: query.page,
                perPage: 200,
                keyword: query?.search == "" ? undefined : query?.search,
            });
            const newOptions = data?.data;
            setDiscountCode((prevOptions) => [...prevOptions, ...newOptions]);
            setHasMore(data.lastPage == query.page ? false : true);
            setDiscountCodeLoading(false);
        } catch (error) {
        } finally {
            setDiscountCodeLoading(false);
        }
    };

    useEffect(() => {
        getDiscountCode();
    }, [direction, query]);

    const fieldNames = [
        "homeHeaderClientEn",
        "homeHeaderClientAr",
        "homeHeaderDriverEn",
        "homeHeaderDriverAr",
        "homeHeaderInvestorEn",
        "homeHeaderInvestorAr",
        "homeHeaderClient",
        "homeHeaderDriver",
        "homeHeaderInvestor",
        "imageIdSender",
        "imageIdForBigMessage",
        "imageIdForSmallMessage",
        "imageIdReceiver",
        "imageIdForVerySmallMessage"
    ];

    const invitationImageFields = [
        "imageIdSender",
        "imageIdReceiver",
        "imageIdForBigMessage",
        "imageIdForSmallMessage",
        "imageIdForVerySmallMessage"
    ];

    const headerImageFields = [
        "homeHeaderClient",
        "homeHeaderClientEn",
        "homeHeaderClientAr",
        "homeHeaderDriver",
        "homeHeaderDriverEn",
        "homeHeaderDriverAr",
        "homeHeaderInvestor",
        "homeHeaderInvestorEn",
        "homeHeaderInvestorAr",
    ];

    // Watch all image fields at once
    const watchedFields = useWatch({ control, name: fieldNames });

    useEffect(() => {
        if (!list) return;

        const updatedImageChanged: Record<string, boolean> = {};
        fieldNames.forEach((field, index) => {
            const currentId = watchedFields?.[index];
            const originalId = list?.[field]?.id;
            updatedImageChanged[field] = currentId !== originalId;
        });
        setImageChanged(updatedImageChanged);
    }, [JSON.stringify(watchedFields), list]);

    // ========== Tab 1: التسعير والدفع ==========
    const PricingPaymentTab = () => (
        <Collapse
            defaultActiveKey={['currency', 'ePayment', 'loyalty']}
            items={[
                {
                    key: 'currency',
                    label: t("currency_and_pricing"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={12} md={12} lg={12} key={`currency-${index}`}>
                                    <FieldBuilder
                                        name={`currencySymbol[${lang?.code}]`}
                                        label={`${t("currencySymbol")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="exchangePrice" label={t("exchange_price")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="commission" label={t("commission")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="minimumLimit" label={t("minimum_limit")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="upperLimit" label={t("upper_limit")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'ePayment',
                    label: t("e_payment_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("isEPaymentEnabled")} name="isEPaymentEnabled" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("isAlbarakaPaymentEnabled")} name="isAlbarakaPaymentEnabled" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("isAlshamPaymentEnable")} name="isAlshamPaymentEnable" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder name="dailyPaymentLimit" label={t("dailyPaymentLimit")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder name="weeklyPaymentLimit" label={t("weeklyPaymentLimit")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder name="monthlyPaymentLimit" label={t("monthlyPaymentLimit")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'loyalty',
                    label: t("loyalty_and_rewards"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="pointPrice" label={t("pointPrice")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="pointMultiplicationFactor" label={t("pointMultiplicationFactor")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="scoreValidity" label={t("scoreValidity")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="loyaltyProgramLink" label={t("loyaltyProgramLink")} input={{ type: "text" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 2: التوصيل والطلبات ==========
    const DeliveryOrdersTab = () => (
        <Collapse
            defaultActiveKey={['deliveryPricing', 'deliveryOptions', 'driver', 'pharmacy', 'orders']}
            items={[
                {
                    key: 'deliveryPricing',
                    label: t("delivery_pricing"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="kilometerCost" label={t("kilometerCost")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="priceKilometerCustomer" label={t("priceKilometerCustomer")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="fixedDeliveryPriceOnClient" label={t("fixed_delivery_price_on_client")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="priceTotalProductsToGetFreeDelivery" label={t("priceTotalProductsToGetFreeDelivery")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="expressDeliveryPrice" label={t("express_delivery_price")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'deliveryOptions',
                    label: t("delivery_options"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("freeDelivery")} name="freeDelivery" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("freeDeliveryPharmacyPromotion")} name="freeDeliveryPharmacyPromotion" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("express_delivery_service")} name="expressDeliveryService" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("use_trip_based_delivery")} name="useTripBasedDelivery" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("include_return_trip_in_payment")} name="includeReturnTripInPayment" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("first_order_free")} name="firstOrderFree" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder
                                    name="routeStrategy"
                                    label={t("route_strategy")}
                                    input={{
                                        type: "select",
                                        options: [
                                            { label: t("strategy_optimized"), value: 1 },
                                            { label: t("strategy_hybrid_shortest"), value: 2 },
                                            { label: t("strategy_nearest_neighbor"), value: 3 },
                                        ],
                                    }}
                                    rules={{ required: false }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("enable_route_comparison")} name="enableRouteComparison" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="fixedDeliveryTime" label={t("fixed_delivery_time_minutes")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="expressDeliveryTime" label={t("express_delivery_time_minutes")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'driver',
                    label: t("driver_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="deliveryWorkerAcceptanceTime" label={t("deliveryWorkerAcceptanceTime")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="driverTimeBroadcast" label={t("driverTimeBroadcast")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxDriversInOrder" label={t("maxDriversInOrder")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="radiusSearchForDriver" label={t("radiusSearchForDriver")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="numberTimesIgnoredAllowed" label={t("numberTimesIgnoredAllowed")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'pharmacy',
                    label: t("pharmacy_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="pharmacyAcceptanceTime" label={t("pharmacyAcceptanceTime")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxNumberPharmaciesFindPharmacie" label={t("maxNumberPharmaciesFindPharmacie")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxNumberPharmaciesFindMedicine" label={t("maxNumberPharmaciesFindMedicine")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("showListPharmacies")} name="showListPharmacies" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'orders',
                    label: t("order_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="clientAcceptanceTime" label={t("client_acceptance_time")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="timeBroadcast" label={t("timeBroadcast")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxNumOfUnResponseOrder" label={t("maxNumOfUnResponseOrder")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="delayedOrderSchedulingTime" label={t("delayedOrderSchedulingTime")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 3: التطبيق والتواصل ==========
    const AppCommunicationTab = () => (
        <Collapse
            defaultActiveKey={['publicMessages', 'welcome', 'sharing', 'social', 'general']}
            items={[
                {
                    key: 'publicMessages',
                    label: t("public_messages"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgClient-${index}`}>
                                    <FieldBuilder
                                        name={`publicMessageClient[${lang?.code}]`}
                                        label={`${t("publicMessageClient")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgPharmacist-${index}`}>
                                    <FieldBuilder
                                        name={`publicMessagePharmacist[${lang?.code}]`}
                                        label={`${t("publicMessagePharmacist")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgDelivery-${index}`}>
                                    <FieldBuilder
                                        name={`publicMessageDelivery[${lang?.code}]`}
                                        label={`${t("publicMessageDlivery")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("activatePublicMessageClient")} name="activatePublicMessageClient" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("activatePublicMessagePharmacist")} name="activatePublicMessagePharmacist" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("activatePublicMessageDelivery")} name="activatePublicMessageDelivery" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'welcome',
                    label: t("welcome_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`welcome-${index}`}>
                                    <FieldBuilder
                                        name={`welcomeMessage[${lang?.code}]`}
                                        label={`${t("welcome_message")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("welcomeMessageActivation")} name="welcomeMessageActivation" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder
                                    name="welcomeCoupon"
                                    label={t("welcomeCoupon")}
                                    width="large"
                                    input={{
                                        type: "select-pagination",
                                        handleScroll: handleScroll,
                                        handleSearch: handleSearch,
                                        loading: discountCodeLoading,
                                        allowSearch: true,
                                        options: discountCode?.map((code, index) => ({
                                            key: index,
                                            label: code.name,
                                            value: code.id,
                                        })),
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("welcomeCouponActive")} name="welcomeCouponActive" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'sharing',
                    label: t("app_sharing"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <FieldBuilder name="sharingAppLink" label={t("sharingAppLink")} input={{ type: "text" }} rules={{ required: true }} />
                            </Col>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={12} md={12} lg={12} key={`sharing-${index}`}>
                                    <FieldBuilder
                                        name={`sharingAppMessage[${lang?.code}]`}
                                        label={`${t("sharingAppMessage")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'social',
                    label: t("social_contact"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="facebook" label={t("facebook")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="instagram" label={t("instagram")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="telegram" label={t("telegram")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="whatsApp" label={t("whatsApp")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="phone" label={t("phone")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="hotLine" label={t("hotLine")} input={{ type: "text" }} rules={{ required: false, minLength: 4, maxLength: 7 }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'general',
                    label: t("general_options"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("activatingPublicService")} name="activatingPublicService" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("showMedicalSpecialties")} name="showMedicalSpecialties" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("showCompanies")} name="showCompanies" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("otp_service_unavailable")} name="otpServiceUnavailable" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="circleRadiusMeters" label={t("circleRadiusMeters")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="newProductPeriodDays" label={t("new_product_period_days")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 4: الدعوات ==========
    const InvitationsTab = () => (
        <Collapse
            defaultActiveKey={['invitationEnable', 'senderMessages', 'receiverMessages', 'invitationContent', 'invitationImages']}
            items={[
                {
                    key: 'invitationEnable',
                    label: t("invitation_activation"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col span={24}>
                                <FieldBuilder label={t("enable_invitation")} name="enableInvitation" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'senderMessages',
                    label: t("sender_messages"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`titleSender-${index}`}>
                                    <FieldBuilder
                                        name={`titleInvitationSender[${lang?.code}]`}
                                        label={`${t("title_invitation_sender")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`descSender-${index}`}>
                                    <FieldBuilder
                                        name={`descriptionInvitationSender[${lang?.code}]`}
                                        label={`${t("description_invitation_sender")} (${lang?.name})`}
                                        input={{ type: "text-area" }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'receiverMessages',
                    label: t("receiver_messages"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`titleReceiver-${index}`}>
                                    <FieldBuilder
                                        name={`titleInvitationReceiver[${lang?.code}]`}
                                        label={`${t("title_invitation_receiver")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`descReceiver-${index}`}>
                                    <FieldBuilder
                                        name={`descriptionInvitationReceiver[${lang?.code}]`}
                                        label={`${t("description_invitation_receiver")} (${lang?.name})`}
                                        input={{ type: "text-area" }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'invitationContent',
                    label: t("invitation_content"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgShare-${index}`}>
                                    <FieldBuilder
                                        name={`messageForSharingInvitation[${lang?.code}]`}
                                        label={`${t("message_for_sharing_invitation")} (${lang?.name})`}
                                        input={{ type: "text-area" }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col span={24} key={`smallMsg-${index}`}>
                                    <FormItem label={`${t("small_message_for_invitation")} (${lang?.name})`}>
                                        <Controller
                                            name={`smallMessageForInvitation[${lang?.code}]`}
                                            rules={{ required: { value: false, message: t("field_is_required_message") } }}
                                            render={({ field: { ref, ...field } }) => (
                                                <RichEditor value={field?.value ?? ""} onChange={field?.onChange} />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col span={24} key={`bigMsg-${index}`}>
                                    <FormItem label={`${t("big_message_for_invitation")} (${lang?.name})`}>
                                        <Controller
                                            name={`bigMessageForInvitation[${lang?.code}]`}
                                            rules={{ required: { value: false, message: t("field_is_required_message") } }}
                                            render={({ field: { ref, ...field } }) => (
                                                <RichEditor value={field?.value ?? ""} onChange={field?.onChange} />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`verySmallMsg-${index}`}>
                                    <FormItem label={`${t("very_small_message_for_invitation")} (${lang?.name})`}>
                                        <Controller
                                            name={`verySmallMessageForInvitation[${lang?.code}]`}
                                            rules={{ required: { value: false, message: t("field_is_required_message") } }}
                                            render={({ field: { ref, ...field } }) => (
                                                <RichEditor value={field?.value ?? ""} onChange={field?.onChange} />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'invitationImages',
                    label: t("invitation_images"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {invitationImageFields.map((field) => (
                                <ImageFieldBlock
                                    key={field}
                                    fieldName={field}
                                    currentImage={list?.[field]}
                                    newImageUrl={newImages[field]}
                                    isChanged={imageChanged[field]}
                                    onUpload={() => handleUploadNewImage(field)}
                                    onUpdateBackground={() => handleUpdateBackground(field, list?.[field]?.id)}
                                    onImageChange={handleImageChange(field)}
                                    setWithoutBackgrounds={(value) => setWithoutBackgrounds((prev) => ({ ...prev, [field]: value }))}
                                    onDeleteImage={() => handleImageDelete(field)}
                                    isImageLoading={imageLoading[field]}
                                    value={withoutBackgrounds[field]}
                                />
                            ))}
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 5: المستثمرين والصور ==========
    const InvestorsMediaTab = () => (
        <Collapse
            defaultActiveKey={['investorVisibility', 'headerImages']}
            items={[
                {
                    key: 'investorVisibility',
                    label: t("investor_visibility"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("makeInvestorsNoResponseOffVisibility")} name="makeInvestorsNoResponseOffVisibility" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("ordersSummeryInvestorVisibility")} name="ordersSummeryInvestorVisibility" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("ordersSummeryGuestInvestorVisibility")} name="ordersSummeryGuestInvestorVisibility" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="startDateTimeOrdersSummeryInvestor" label={t("startDateTimeOrdersSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="endDateTimeOrdersSummeryInvestor" label={t("endDateTimeOrdersSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="startDateTimeOrdersGuestSummeryInvestor" label={t("startDateTimeOrdersGuestSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="endDateTimeOrdersGuestSummeryInvestor" label={t("endDateTimeOrdersGuestSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'headerImages',
                    label: t("header_images"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {headerImageFields.map((field) => (
                                <ImageFieldBlock
                                    key={field}
                                    fieldName={field}
                                    currentImage={list?.[field]}
                                    newImageUrl={newImages[field]}
                                    isChanged={imageChanged[field]}
                                    onUpload={() => handleUploadNewImage(field)}
                                    onUpdateBackground={() => handleUpdateBackground(field, list?.[field]?.id)}
                                    onImageChange={handleImageChange(field)}
                                    setWithoutBackgrounds={(value) => setWithoutBackgrounds((prev) => ({ ...prev, [field]: value }))}
                                    onDeleteImage={() => handleImageDelete(field)}
                                    isImageLoading={imageLoading[field]}
                                    value={withoutBackgrounds[field]}
                                />
                            ))}
                        </Row>
                    ),
                },
            ]}
        />
    );

    const tabItems = [
        {
            key: 'pricing',
            label: <span><DollarOutlined /> {t("pricing_and_payment")}</span>,
            children: <PricingPaymentTab />,
        },
        {
            key: 'delivery',
            label: <span><CarOutlined /> {t("delivery_and_orders")}</span>,
            children: <DeliveryOrdersTab />,
        },
        {
            key: 'app',
            label: <span><MobileOutlined /> {t("app_and_communication")}</span>,
            children: <AppCommunicationTab />,
        },
        {
            key: 'invitations',
            label: <span><GiftOutlined /> {t("invitations")}</span>,
            children: <InvitationsTab />,
        },
        {
            key: 'investors',
            label: <span><TeamOutlined /> {t("investors_and_media")}</span>,
            children: <InvestorsMediaTab />,
        },
    ];

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.contentWrapper}>
                    <span className={styles.title}>
                        {t("update_general_settings")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("list") ? (
                        <div className={styles.loadingWrapper}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Tabs
                                defaultActiveKey="pricing"
                                items={tabItems}
                                size="middle"
                                tabBarStyle={{ marginBottom: 8 }}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.stickyFooter}>
                    <Button
                        onClick={() => {
                            reset();
                            navigate(-1);
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button loading={loadingSubmit} htmlType="submit" type="primary">
                        {t("save")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

const GeneralSettingForm = () => {
    return (
        <GeneralSettingContextProvider>
            <Form />
        </GeneralSettingContextProvider>
    );
};

export default GeneralSettingForm;
